// 엔드포인트 URL 파라미터 계약 — endpoints(shared)가 소유. feature 역참조 방지를 위해
// 도메인 union은 string으로 완화(빌더는 문자열 interpolate만 하므로 런타임 동일, 호출부 강타입은 대입 가능).
type TProductSort = string

interface IGetMycleContentsParams {
  menu: string
  page?: number
  size?: number
  sort?: string
  categoryId?: number
}
interface IGetMycleContentCategoriesParams {
  menu: string
}
interface IGetMycleContentsBySeriesParams {
  code: string
  page?: number
  size?: number
}
interface IGetProductListByPlaceAndVehicleParams {
  standardId: number
  placeHashId: string
  sections: readonly string[]
  fuelType: string
  /** 타이어 규격. 비우면 서버가 차량 순정 규격으로 매칭한다 */
  tire?: string
}
interface IDiariesByClsfParams {
  socialId: string
  macarObjectId: string
  clsf?: string
  upperDate?: string
  page?: number
  maxGetDiarySize?: number
}
interface IDiariesBySocialIdParams {
  socialId: string
  page?: number
  maxGetDiarySize?: number
}
interface IUserPlaceParams {
  socialId: string
  objectId: string
}

const getApiPath = (path: string) => {
  return process.env.NODE_ENV === 'production' ? `/v2${path}` : path
}

export const CLIENT_API_ENDPOINTS = {
  AUTH: {
    KAKAO: {
      SIGNUP: `${getApiPath('/api/auth/signup/kakao')}`,
      LOGIN: `${getApiPath('/api/auth/login/kakao')}`,
    },
    SIGNUP: `${getApiPath('/api/auth/signup')}`,
    LOGIN: `${getApiPath('/api/auth/login')}`,
    LOGOUT: `${getApiPath('/api/auth/logout')}`,
  },
  USER: {
    INFO: `${getApiPath('/api/user')}`,
    RUM_ID: `${getApiPath('/api/user/rum-id')}`,
  },
  COUPON: {
    FOR_NEW_USERS: `${getApiPath('/api/coupon/new-users/products')}`,
    FOR_RESERVATION: `${getApiPath('/api/coupon/reservation/list')}`,
    FOR_ON_SITE_RESERVATION: `${getApiPath('/api/coupon/on-site-reservation/list')}`,
    REGISTER_FOR_RESERVATION: `${getApiPath('/api/coupon/reservation/register')}`,
    /** 상품 그룹에 걸린 최대 할인 쿠폰 자동 발급 */
    MAX_DISCOUNT_GROUP: `${getApiPath('/api/coupon/reservation/max-discount-group')}`,
  },
  /*
    정비소 찾기(SEO 유입 전용, WEB-5774). 아래 `PLACE`와 **다른 라우트**다.
    그쪽은 인앱 웹뷰 검색이 쓰고, 이쪽은 제휴 업체를 당겨 정렬한 결과를 준다.
    같은 주소를 쓰면 두 화면이 서로의 동작을 바꾸게 된다.
  */
  PLACE_FINDER: {
    SEARCH: `${getApiPath('/api/place-finder/search')}`,
  },
  PLACE: {
    SEARCH: `${getApiPath('/api/place/search')}`,
    LIST: `${getApiPath('/api/place/list')}`,
    ENCODE_HASH_ID: (placeId: number) =>
      `${getApiPath(`/api/place/hash-id/encode/${placeId}`)}`,
    DETAIL_BY_HASH_ID: (hashId: string) =>
      `${getApiPath(`/api/place/${hashId}`)}`,
    DETAIL_BY_SERVER_ID: (serverId: number) =>
      `${getApiPath(`/api/place/detail/${serverId}`)}`,
  },
  PRODUCT: {
    GROUP_LIST: `${getApiPath('/api/product/group/list')}`,
    DETAIL_GROUP: (productGroupId: number) =>
      `${getApiPath(`/api/product/group/${productGroupId}`)}`,
    LIST_BY_PLACE_AND_VEHICLE: ({
      placeHashId,
      standardId,
      sections,
      fuelType,
      tire,
    }: IGetProductListByPlaceAndVehicleParams) =>
      `${getApiPath(`/api/product/list?standardId=${standardId}&placeHashId=${placeHashId}&sections=${sections.join(',')}&fuelType=${fuelType}${tire ? `&tire=${encodeURIComponent(tire)}` : ''}`)}`,
    LIST_BY_GROUP: ({
      productGroupId,
      sort,
    }: {
      productGroupId: number
      sort?: TProductSort
    }) =>
      `${getApiPath(`/api/product/group/${productGroupId}/products${sort ? `?sort=${sort}` : ''}`)}`,
  },
  CAR: {
    BRAND: `${getApiPath('/api/car/brand')}`,
    MODEL: (brandId: number) => `${getApiPath(`/api/car/model/${brandId}`)}`,
    DETAIL_MODEL: ({ brandId, carId }: { brandId: number; carId: number }) =>
      `${getApiPath(`/api/car/detail-model?brandId=${brandId}&carId=${carId}`)}`,
    VEHICLE_OPTIONS: ({
      standardId,
      fuelType,
    }: {
      standardId: number
      fuelType?: string
    }) =>
      `${getApiPath(`/api/car/vehicle-options?standardId=${standardId}${fuelType ? `&fuelType=${fuelType}` : ''}`)}`,
    CREATE_MY_CAR_BY_MODEL: `${getApiPath('/api/car/create/model')}`,
    MACAR: `${getApiPath('/api/car/macar')}`,
    MACAR_DETAIL: (macarId: number) =>
      `${getApiPath(`/api/car/macar/${macarId}`)}`,
    CAR_INFO_WEB: `${getApiPath('/api/car/carinfo-web')}`,
  },
  CAR_HUB: {
    PLACES: `${getApiPath('/api/carHub/places')}`,
    LIST: `${getApiPath('/api/carHub/list')}`,
  },

  RESERVATION: {
    CREATE: `${getApiPath('/api/reservation/create')}`,
    DETAIL: (reservationId: string) =>
      `${getApiPath(`/api/reservation/${reservationId}`)}`,
    DELETE: (reservationId: string) =>
      `${getApiPath(`/api/reservation/delete/${reservationId}`)}`,
    CHANGE_PRODUCTS: (reservationId: number) =>
      `${getApiPath(`/api/reservation/${reservationId}/products`)}`,
    UPDATE_BUYER_INFO: (id: number) =>
      `${getApiPath(`/api/reservation/${id}/buyer-info`)}`,
    /**
     * 예약 종류를 가리지 않는 일정 조회.
     * on-site 밑의 것과 하는 일은 같지만 이름이 현장결제를 암시해서 따로 둔다.
     * 엔진오일 예약 등이 생기면 이 경로를 함께 쓴다.
     */
    SCHEDULE: {
      /** 여러 업체의 특정 날짜 시간 슬롯 */
      HOURLY_BULK: `${getApiPath('/api/reservation/schedule/hourly-bulk')}`,
      /** 여러 업체의 날짜별 예약 가능 여부(2주치) */
      DAILY_BULK: `${getApiPath('/api/reservation/schedule/daily-bulk')}`,
    },
    PLACE: {
      /** 예약 가능한 업체 목록. 시간은 빠져 있다(SCHEDULE 쪽이 맡는다) */
      LIST: `${getApiPath('/api/reservation/place/list')}`,
    },
    PRODUCT: {
      /** 한 섹션(타이어 등)의 상품 목록. 그룹 조회까지 BFF 가 묶어 준다 */
      LIST: `${getApiPath('/api/reservation/product/list')}`,
      /** 서비스 소개의 타이어 최저가 카드. 그룹 상품에서 직접 고른다 */
      TIRE_CARD: `${getApiPath('/api/reservation/product/tire-card')}`,
      ADDITIONAL: `${getApiPath('/api/reservation/product/additional')}`,
    },
    AVAILABILITY: `${getApiPath('/api/reservation/schedule/availability')}`,
    BUYER_INFO: `${getApiPath('/api/reservation/buyer-info')}`,
  },
  TERMS: {
    DETAIL: (type: string) => `${getApiPath(`/api/terms/${type}`)}`,
  },
  VERIFICATION: {
    REQUEST_SMS_VERIFICATION_CODE: () =>
      `${getApiPath(`/api/verification/sms/request-code`)}`,
    VERIFY_SMS_VERIFICATION_CODE: () =>
      `${getApiPath(`/api/verification/sms/check-code`)}`,
  },
  PAYMENT: {
    KCP: {
      APPROVE: `${getApiPath('/api/payment/kcp/approve')}`,
      PC: {
        BEGIN: `${getApiPath('/api/payment/kcp/pc/begin')}`,
      },
      MOBILE: {
        BEGIN: `${getApiPath('/api/payment/kcp/mobile/begin')}`,
        CALLBACK: `${getApiPath('/api/payment/kcp/mobile/callback')}`,
      },
      VBANK: {
        BEGIN: `${getApiPath('/api/payment/kcp/vbank/begin')}`,
        ISSUE: `${getApiPath('/api/payment/kcp/vbank/issue')}`,
      },
    },
    TOSSPAYMENTS: {
      BEGIN: `${getApiPath('/api/payment/tosspayments/begin')}`,
      APPROVE: `${getApiPath('/api/payment/tosspayments/approve')}`,
    },
    INSTALLMENT_INFO: `${getApiPath('/api/payment/installment-info')}`,
    ON_SITE: `${getApiPath('/api/payment/on-site')}`,
    AVAILABLE_METHOD: `${getApiPath('/api/reservation/payment/available-method')}`,
  },
  ADDITIONAL_PAYMENT: {
    DETAIL: `${getApiPath('/api/additional-payment')}`,
  },
  PARTNERS: {
    USER: `${getApiPath('/api/partners/user')}`,
    MALL: {
      BANNERS: `${getApiPath('/api/partners/mallBanners')}`,
    },
    VALIDATE: {
      INVITE_CODE: `${getApiPath('/api/partners/validate/invite-code')}`,
      USER_ID: `${getApiPath('/api/partners/validate/user-id')}`,
      OWNER: `${getApiPath('/api/partners/validate/owner')}`,
    },
    SMS: {
      REQUEST_CODE: `${getApiPath('/api/partners/sms/request-code')}`,
      CHECK_CODE: `${getApiPath('/api/partners/sms/check-code')}`,
      SIGNUP_REQUEST_CODE: `${getApiPath('/api/partners/sms/signup/request-code')}`,
      SIGNUP_CHECK_CODE: `${getApiPath('/api/partners/sms/signup/check-code')}`,
      USER_REQUEST_CODE: `${getApiPath('/api/partners/sms/user/request-code')}`,
      USER_CHECK_CODE: `${getApiPath('/api/partners/sms/user/check-code')}`,
    },
    ACCOUNT: {
      CHANGE_EMAIL: `${getApiPath('/api/partners/account/change-email')}`,
      RESET_PASSWORD: `${getApiPath('/api/partners/account/reset-password')}`,
      VALIDATE_PASSWORD: `${getApiPath('/api/partners/account/validate-password')}`,
      CHANGE_CONTACT: `${getApiPath('/api/partners/account/change-contact')}`,
      SIGNUP: `${getApiPath('/api/partners/account/signup')}`,
    },
    APPLY: {
      DOCUMENTS: `${getApiPath('/api/partners/apply/documents')}`,
      BUSINESS_TYPE: `${getApiPath('/api/partners/apply/business-type')}`,
      PRESIGNED_URL: `${getApiPath('/api/partners/apply/presigned-url')}`,
      DELETE_DOCUMENT: (serverId: number) =>
        `${getApiPath(`/api/partners/apply/documents/${serverId}`)}`,
      USER: {
        UPDATE_STATUS: (serverId: number, event: string) =>
          `${getApiPath(`/api/partners/apply/user/${serverId}/status?event=${event}`)}`,
      },
    },
    ON_SITE: {
      DECODE_PLACE_HASH_ID: (placeHashId: string) =>
        `${getApiPath(`/api/on-site/hash-id/place/decode/${placeHashId}`)}`,
      DAILY_SCHEDULE: `${getApiPath('/api/on-site/schedule/daily')}`,
      HOURLY_SCHEDULE: `${getApiPath('/api/on-site/schedule/hourly')}`,
    },
  },
  DIARY: {
    CREATE: `${getApiPath('/api/diary/create')}`,
  },
  MAINTENANCE: {
    CREATE_DIARY: (socialId: string) =>
      `${getApiPath(`/api/maintenance/diary/create?socialId=${socialId}`)}`,
    DIARY_SUGGESTION: (macarId: number) =>
      `${getApiPath(`/api/maintenance/diary-suggestion?macarId=${macarId}`)}`,
  },
  CARWASH: {
    /**
     * 좌표 기준 날씨 예보.
     * 세차 관리 화면에서 유저가 지역을 바꿨을 때만 부른다
     * (처음 그릴 때는 서버가 묶어서 내려준다).
     */
    WEATHER: ({
      latitude,
      longitude,
    }: {
      latitude: number
      longitude: number
    }) =>
      `${getApiPath(`/api/carwash/weather?latitude=${latitude}&longitude=${longitude}`)}`,
  },
  RECORD_CERTIFICATE: {
    PUBLIC_INFO: (macarId: number) =>
      `${getApiPath(`/api/record-certificate/${macarId}/public-info`)}`,
    GET: (macarId: number) =>
      `${getApiPath(`/api/record-certificate/${macarId}`)}`,
    CREATE: (macarId: number) =>
      `${getApiPath(`/api/record-certificate/${macarId}`)}`,
  },
  SETTINGS: {
    DISABLE_MARKETING_NOTIFICATION: `${getApiPath('/api/settings/marketing-notification/disable')}`,
  },
  HARSH_DRIVING: {
    MODIFY_TODO_TYPE: `${getApiPath('/api/harsh-driving/modify-todo-type')}`,
  },
  MISSION: {
    WEEKLY: `${getApiPath('/api/mission/weekly')}`,
    ROULETTE: `${getApiPath('/api/mission/roulette')}`,
    STATUS: `${getApiPath('/api/mission/status')}`,
    PRIZES: `${getApiPath('/api/mission/prizes')}`,
    TICKETS_COUNT: `${getApiPath('/api/mission/tickets/count')}`,
  },
  REPORT: {
    MONTHLY_MAINTENANCE: `${getApiPath('/api/report/monthly-maintenance')}`,
    MONTHLY_MAINTENANCE_SHARED: `${getApiPath('/api/report/monthly-maintenance/shared')}`,
    TIRE_TREAD: (reservationId: number) =>
      `${getApiPath(`/api/report/tire-tread/${reservationId}`)}`,
  },
  CAR_STORY: {
    HOME: `${getApiPath('/api/car-story/home')}`,
    CONTENTS: ({
      menu,
      page = 0,
      size = 20,
      sort,
      categoryId,
    }: IGetMycleContentsParams) =>
      `${getApiPath(`/api/car-story/contents?menu=${menu}&page=${page}&size=${size}${sort ? `&sort=${encodeURIComponent(sort)}` : ''}${categoryId != null ? `&categoryId=${categoryId}` : ''}`)}`,
    CATEGORIES: ({ menu }: IGetMycleContentCategoriesParams) =>
      `${getApiPath(`/api/car-story/categories?menu=${menu}`)}`,
    SERIES: ({ code, page = 0, size = 20 }: IGetMycleContentsBySeriesParams) =>
      `${getApiPath(`/api/car-story/series/${code}?page=${page}&size=${size}`)}`,
    DETAIL: (contentId: number) =>
      `${getApiPath(`/api/car-story/contents/${contentId}`)}`,
    LIKE: (contentId: number) =>
      `${getApiPath(`/api/car-story/contents/${contentId}/like`)}`,
    UNLIKE: (contentId: number) =>
      `${getApiPath(`/api/car-story/contents/${contentId}/unlike`)}`,
    NEAREST_PLACES: ({
      latitude,
      longitude,
    }: {
      latitude: number
      longitude: number
    }) =>
      `${getApiPath(`/api/car-story/nearest-places?lat=${latitude}&lng=${longitude}`)}`,
    RECOMMENDATIONS: ({
      exposureArea,
      sections,
      limit,
      macarId,
    }: {
      exposureArea: string
      sections: string[]
      limit: number
      macarId: number
    }) => {
      const params = new URLSearchParams({
        exposureArea,
        limit: String(limit),
        macarId: String(macarId),
      })
      sections.forEach(section => params.append('sections', section))
      return `${getApiPath(`/api/car-story/recommendations?${params.toString()}`)}`
    },
  },
} as const

// SEO 인근 정비소 기본 size — 호출부에서 자기 자신 제외 여유분 포함해 호출
const SEO_NEARBY_DEFAULT_SIZE = 11

export const SERVER_API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/login',
    SIGNUP: '/signup',
  },
  USER: {
    INFO: '/user',
  },
  COUPON: {
    FOR_NEW_USERS: '/commerce/v1/newUsers/commerceProducts',
    FOR_RESERVATION: '/reservation/coupon',
    /**
     * 상품 그룹 기준 최대 할인 쿠폰 발급 (앱 `createMaxDiscountCouponGroupByProductGroup`).
     * ⚠️ 발급 대상이 아니면 **204 (본문 없음)** 로 온다 — 에러가 아니다.
     */
    MAX_DISCOUNT_GROUP: '/reservation/couponGroup/maxDiscount',
    /**
     * socialId 기준 유저 쿠폰 발급 (서버 전용).
     *
     * ⚠️ **차량(macarId)을 요구하지 않는 유일한 발급 API다.** 같은 계열의
     * `/server/v1/coupons`는 `macarId`가 필수라, 차량을 등록하지 않는
     * 제휴 랜딩에서는 쓸 수 없다.
     */
    ISSUE_TO_USER: ({
      socialId,
      templateId,
    }: {
      socialId: string
      templateId: number
    }) =>
      `/reservation/server/v1/userCoupons?socialId=${encodeURIComponent(socialId)}&templateId=${templateId}`,
  },
  PLACE: {
    TARGET_LIST: '/targetProduct/targetPlace',
    TARGET_LIST_HOURLY_SCHEDULE: '/reservation/hourlySchedules',
    TARGET_LIST_DAILY_SCHEDULE: '/reservation/dailySchedule',
    /** 여러 업체의 날짜별 예약 가능 여부. placeIds 를 쉼표로 묶어 한 번에 받는다 */
    DAILY_SCHEDULES: '/reservation/v1/schedules/daily',
    /** 고른 시간에 실제로 예약이 되는지 (예약 직전 확인) */
    SCHEDULE_AVAILABILITY: '/reservation/v1/schedules/availability',
    /** 업체 한 곳의 시간별 슬롯. 복수 업체용(hourlySchedules)과 다른 엔드포인트다 */
    HOURLY_SCHEDULE: '/reservation/hourlySchedule',
    SEARCH: (searchParams: string) => `/place-v3/place?${searchParams}`,
    TARGET_PLACES_IDS: {
      BY_PRODUCT: '/targetProduct/targetPlaces/byProduct/placeIds',
    },
    ADMIN: {
      TARGET_PRODUCT_GROUPS_BY_EXPOSURE: (exposureId: number) =>
        `/targetProduct/admin/targetProductGroup?exposureId=${exposureId}&fields=serverId`,
      TARGET_PLACES_BY_GROUPS: (targetGroupIds: number[]) =>
        `/targetProduct/admin/targetPlaces?targetGroupIds=${targetGroupIds.join(',')}`,
    },
    LIST: '/place-v3/place',
    SEARCH_LIST: '/place-v3/place/search',
    USER_PLACE: ({ socialId, objectId }: IUserPlaceParams) =>
      `/macar-v2/admin/places?socialId=${encodeURIComponent(socialId)}&objectId=${objectId}`,
    DETAIL: (serverId: string) => `/place-v3/place/${serverId}`,
    SEO_DETAIL: (serverId: string | number) =>
      `/place-v3/place/${serverId}/seo`,
    NEARBY: ({
      latitude,
      longitude,
      maxDistance = 30,
      size = 11,
    }: {
      latitude: number | string
      longitude: number | string
      maxDistance?: number
      size?: number
    }) =>
      `/place-v3/admin/place?category=TRUSTED_M&lat=${latitude}&lng=${longitude}&maxDistance=${maxDistance}&page=0&size=${size}&isTrustOnly=false`,
    SEO_NEARBY: ({
      latitude,
      longitude,
      maxDistance = 30,
      size = SEO_NEARBY_DEFAULT_SIZE,
      category = 'TRUSTED_M',
    }: {
      latitude: number | string
      longitude: number | string
      maxDistance?: number
      size?: number
      category?: string
    }) => {
      const params = new URLSearchParams({
        lat: String(latitude),
        lng: String(longitude),
        maxDistance: String(maxDistance),
        page: '0',
        size: String(size),
      })
      if (category) params.set('category', category)
      return `/place-v3/place/seo/nearby?${params.toString()}`
    },
    INTRODUCTION: (serverId: number | string) =>
      `/place-v3/place/${serverId}/introduction`,
    REVIEW_SUMMARY: (serverId: number | string) =>
      `/place-v3/place/${serverId}/review-summary`,
    IMAGE_PRESIGNED_URL: ({
      version,
      objectType,
      objectId,
      filename,
    }: {
      version: 'v3' | 'v4'
      objectType: string
      objectId: string
      filename: string
    }) =>
      `/${version}/${objectType}/${objectId}/image/presignedUrl?method=GET&filename=${encodeURIComponent(filename)}`,
  },
  SOCIAL: {
    // filterMode=reservationFlow: 마이클 앱 정비소 상세와 동일 기준 (#상품리뷰 태그 = type 302/303만)
    PLACE_REVIEWS: (placeId: number | string) =>
      `/social-v2/placeReviews?placeId=${placeId}&page=0&onlyHasImage=false&filterMode=reservationFlow`,
    POSITIVE_REVIEW_RATE: (placeId: number | string) =>
      `/social-v2/reviewSentiments/rate?placeId=${placeId}`,
    /** 차종 허브 - 모두의차고 정비 리뷰 (brand+model 기준) */
    MAINTENANCE_FEEDS: ({
      brand,
      model,
      page = 0,
      count = 10,
    }: {
      brand: string
      model: string
      page?: number
      count?: number
    }) =>
      `/social-v2/maintenanceFeeds?brand=${encodeURIComponent(brand)}&model=${encodeURIComponent(model)}&page=${page}&count=${count}`,
    MAINTENANCE_FEED_COUNT: ({
      brand,
      model,
    }: {
      brand: string
      model: string
    }) =>
      `/social-v2/maintenanceFeedCount?brand=${encodeURIComponent(brand)}&model=${encodeURIComponent(model)}`,
    /** 상품군 리뷰 최신순 (groupType 예: 엔진오일, 엔진오일(수입차)) */
    PRODUCT_REVIEWS: ({
      groupType,
      size,
    }: {
      groupType: string
      size: number
    }) =>
      `/social-v2/v3/productReviews?groupType=${encodeURIComponent(groupType)}&size=${size}`,
    REVIEW_SENTIMENTS: ({
      placeId,
      size = 3,
      sentimentResult,
    }: {
      placeId: number | string
      size?: number
      sentimentResult?: 'POSITIVE' | 'NEUTRAL' | 'MIXED' | 'NEGATIVE'
    }) => {
      const params = new URLSearchParams({
        placeId: String(placeId),
        size: String(size),
      })
      if (sentimentResult) params.set('sentimentResult', sentimentResult)
      return `/social-v2/reviewSentiments?${params.toString()}`
    },
  },
  PRODUCT: {
    /** 타이어 최저가와 4본 쿠폰 적용가 */
    TIRE_LOWEST_PRICE: '/reservation/v1/tires/lowestPrice',
    ADDITIONAL: '/targetProduct/additionalProducts',
    GROUP_LIST: '/targetProduct/productGroup',
    DETAIL_GROUP: (productGroupId: string) =>
      `/targetProduct/productGroup/${productGroupId}`,
    DETAIL: (productId: string) =>
      `/targetProduct/admin/targetProduct/${productId}`,
    LIST_BY_PLACE_AND_VEHICLE: ({
      standardId,
      placeId,
      sections,
      fuelType,
      tire,
    }: {
      standardId: number
      placeId: number
      sections: string
      fuelType: string
      /** 타이어 규격. 비우면 서버가 차량 순정 규격으로 매칭한다 */
      tire?: string
    }) =>
      `/targetProduct/productGroups/byPlaceAndVehicle?standardId=${standardId}&placeId=${placeId}&sections=${sections}&fuelType=${fuelType}${tire ? `&tire=${encodeURIComponent(tire)}` : ''}`,
    LIST_BY_GROUP: ({
      productGroupId,
      sort,
    }: {
      productGroupId: number | string
      sort?: TProductSort
    }) =>
      `/targetProduct/productGroups/${productGroupId}/products${sort ? `?sort=${sort}` : ''}`,
    GROUPS_BY_PLACE: (placeId: number | string, exposureAreaId?: number) =>
      `/targetProduct/productGroups?placeId=${placeId}&lowestPrice=true${
        exposureAreaId !== undefined ? `&exposureAreaId=${exposureAreaId}` : ''
      }`,
  },
  CAR: {
    BRAND: '/standard-v2/standard/vehicle/brand',
    MODEL: (brandId: string) =>
      `/standard-v2/standard/vehicle/brand/${brandId}/car`,
    DETAIL_MODEL: ({ brandId, carId }: { brandId: number; carId: number }) =>
      `/standard-v2/standard/vehicle/brand/${brandId}/car/${carId}/model`,
    VEHICLE_OPTIONS: '/standard-v2/vehicleOptions',
    CREATE_MY_CAR_BY_MODEL: '/macar-v2/v2/macar/async',
    MACAR: '/macar-v2/macars',
    MACAR_DETAIL: (macarId: number) => `/macar-v2/macar/${macarId}`,
    MACAR_DETAIL_ADMIN: (macarId: number) =>
      `/macar-v2/admin/macar/${macarId}?withImages=false&deletedInclusive=false`,
    HASH_ID: (macarId: number) => `/web-service/hashid/macar/${macarId}`,
  },
  CAR_HUB: {
    DETAIL: '/report-v3/carHub',
    PLACES: '/report-v3/carHub/places',
    LIST: '/report-v3/carHub/list',
    SITEMAP: '/report-v3/carHub/sitemap',
    FUELS: '/report-v3/carHub/fuels',
  },
  CARISYOU: {
    CAR_INFO_WEB: '/carisyou/carInfo/web',
  },
  RESERVATION: {
    /** 내 구매자 정보 (예약자 이름·연락처) */
    BUYER_INFO: '/reservation/v1/buyerInfo',
    CREATE: '/reservation/reservation',
    DETAIL: (reservationId: string) =>
      `/reservation/reservation/${reservationId}`,
    DELETE: (id: string) => `/reservation/reservation/${id}`,
    CHANGE_PRODUCTS: (reservationId: number) =>
      `/reservation/reservation/${reservationId}/products`,
    UPDATE_BUYER_INFO: (id: number) =>
      `/reservation/reservation/${id}/buyerInfo`,
    /** 내 예약을 상태·상품 섹션으로 조회한다. 없으면 204 */
    BY_USER: '/reservation/reservations/byUser',
    EXTRA_INSPECTION_LATEST: ({
      macarId,
      typeId,
    }: {
      macarId: number
      typeId: number
    }) =>
      `/reservation/reservation/extraInspections/latest?macarId=${macarId}&typeId=${typeId}`,
    // 내차점검 점검 내역 조회 (스케줄 sourceType=INSPECTION 카드용)
    INSPECTION: ({
      inspectionId,
      itemKeys,
    }: {
      inspectionId: number
      itemKeys: string[]
    }) => {
      const params = new URLSearchParams({ inspectionId: String(inspectionId) })
      itemKeys.forEach(key => params.append('itemKeys', key))
      return `/reservation/reservations/inspections?${params.toString()}`
    },
    TIRE_TREAD: (reservationId: string | number) =>
      `/reservation/reservations/${reservationId}/tireTread`,
  },
  TERMS: {
    DETAIL: () => `/operation/terms`,
  },
  VERIFICATION: {
    REQUEST_SMS_VERIFICATION_CODE: () => '/auth/smsAuthentication',
    VERIFY_SMS_VERIFICATION_CODE: () => '/auth/smsVerification',
  },
  PAYMENT: {
    KCP: {
      APPROVE: '/reservation/payment/kcp/approve',
      PC: {
        BEGIN: '/reservation/payment/kcp/pc/begin',
      },
      MOBILE: {
        BEGIN: '/reservation/payment/kcp/mobile/begin',
      },
      /** 무통장입금. 입금 확인은 KCP 가 서버로 알려 승인 API 가 없다 */
      VBANK: {
        BEGIN: '/reservation/payment/kcp/vbank/begin',
        ISSUE: '/reservation/payment/kcp/vbank/issue',
      },
    },
    TOSSPAYMENTS: {
      BEGIN: '/reservation/payment/tosspayments/begin',
      APPROVE: '/reservation/payment/tosspayments/approve',
    },
    /** 무이자 할부 카드 목록. PG 마다 목록이 다르다 */
    INSTALLMENT_INFO: {
      kcp: '/reservation/kcp/promotions/card',
      tosspayments: '/reservation/tosspayments/promotions/card',
    },
    ON_SITE: '/reservation/web/payment/onsite',
    /**
     * 업체별 결제수단. 앱도 이 응답의 `method`·`pg` 를 그대로 써서 결제를 시작한다
     * (앱 `APIService+Reservation.swift:468`).
     */
    AVAILABLE_METHOD: '/reservation/payment/availableMethod',
  },
  // 추가 결제 — 로그인 없이 알림톡 링크 토큰으로 접근하는 공개 API
  ADDITIONAL_PAYMENT: {
    DETAIL: '/reservation/public/additionalReservations/checkout',
    // 결제 시작 — fetch가 아니라 페이지 이동. 서버가 KCP 결제창 form을 렌더한다.
    PAYMENT_START: '/reservation/public/additionalReservations/payment',
  },
  DIARY: {
    CREATE: '/macar-v2/v2/diary',
    CREATE_ADMIN: (socialId: string) =>
      `/macar-v2/admin/v2/diary/socialId/${socialId}`,
    DETAIL_ADMIN: (diaryId: string) => `/macar-v2/admin/diary/${diaryId}`,
    DIARIES_BY_CLSF: ({
      socialId,
      macarObjectId,
      clsf,
      upperDate,
      page,
      maxGetDiarySize,
    }: IDiariesByClsfParams) =>
      `/macar-v2/admin/diaries?socialId=${encodeURIComponent(socialId)}&macarid=${macarObjectId}&clsf=${clsf}${upperDate ? `&upperDate=${upperDate}` : ''}&page=${page}&size=${maxGetDiarySize}`,
    DIARIES_BY_SOCIAL_ID: ({
      socialId,
      page,
      maxGetDiarySize,
    }: IDiariesBySocialIdParams) =>
      `/macar-v2/admin/diaries?socialId=${encodeURIComponent(socialId)}&page=${page}&size=${maxGetDiarySize}`,
    HASH_ID: (diaryId: string) => `/web-service/hashid/diary/${diaryId}`,
  },
  WEEKLY_STATISTICS: {
    OIL_TYPE: ({
      oilType,
      fromDate,
      pastWeekRange = 1,
    }: {
      oilType: string
      fromDate: string
      pastWeekRange?: number
    }) =>
      `/report-v2/oilTypeWeeklyStatistics?oilType=${oilType}&fromDate=${fromDate}&pastWeekRange=${pastWeekRange}`,
    FUEL_EFFICIENCY: ({
      company,
      carName,
      oilType,
      fromDate,
      pastWeekRange = 1,
    }: {
      company: string
      carName: string
      oilType: string
      fromDate: string
      pastWeekRange?: number
    }) =>
      `/report-v2/weeklyStatistics?companyName=${company}&carName=${carName}&oilType=${oilType}&fromDate=${fromDate}&pastWeekRange=${pastWeekRange}`,
  },
  SETTINGS: {
    DISABLE_MARKETING_NOTIFICATION:
      '/notification-v2/public/v1/settings/marketingNotification/disable',
  },
  GAS_VOUCHER: {
    LIST: '/marketing/gasVoucher',
    DETAIL: (gasVoucherId: string) => `/marketing/gasVoucher/${gasVoucherId}`,
  },
  WEEKLY_MISSION: {
    LIST: '/marketing/weeklyMission',
    ROULETTE: '/marketing/weeklyMission/roulette',
    STATUS: '/marketing/weeklyMission/status',
    PRIZES: '/marketing/weeklyMission/prizes',
    TICKETS_COUNT: '/marketing/weeklyMission/tickets/count',
  },
  SCHEDULE: {
    /** 차량별 스케줄 통합 목록 (유저 인증 기반) */
    LIST: (macarId: string) => `/macar-v2/v1/macars/${macarId}/schedules`,
    /** 스케줄 단건 조회·수정 (유저 인증 기반) */
    DETAIL: (serverId: string) => `/macar-v2/v1/schedules/${serverId}`,
  },
  MAINTENANCE: {
    DETAIL: ({ typeId, macarId }: { typeId: number; macarId: number }) =>
      `/macar-v2/v1/macar/${macarId}/maintenances/${typeId}`,
    /**
     * 차종 세부모델별 평균 교체주기 분포
     * intervalTypes: mileage(주행거리) · day(개월). 복수 지정 시 한 응답에 함께 내려온다.
     */
    AVERAGE_INTERVAL_DISTRIBUTION: ({
      macarId,
      productGroupSections,
      intervalTypes,
    }: {
      macarId: number
      productGroupSections: string[]
      intervalTypes: string[]
    }) =>
      `/report-v3/averageIntervalDistribution?macarId=${macarId}&productGroupSections=${productGroupSections.map(encodeURIComponent).join(',')}&intervalTypes=${intervalTypes.join(',')}`,
    /** 정비목록 통합 조회 (유저 인증 기반) — managed/unmanaged 항목에 standard.code 포함 */
    OVERVIEW: (macarId: string) =>
      `/macar-v2/v2/macar/${macarId}/maintenances/overview`,
    /** 정비목록 V3 (유저 인증 기반) */
    OVERVIEW_V3: (macarId: number) =>
      `/macar-v2/v3/macar/${macarId}/maintenances/overview`,
    // 스케줄 상세(V2) — type/maintenanceItem/iconName 통합 응답
    SCHEDULE_DETAIL: ({
      macarId,
      scheduleId,
    }: {
      macarId: number
      scheduleId: number
    }) => `/macar-v2/v2/macar/${macarId}/maintenances/schedules/${scheduleId}`,
    TYPE: {
      DETAIL: (typeId: number) => `/macar-v2/type/${typeId}`,
      /** ⚠️ 전체 교체 PUT — 보내지 않은 필드는 비워지므로 전체 Type을 조회한 뒤 사용 */
      MODIFY: (typeId: number) => `/macar-v2/v2/type/${typeId}`,
    },
    DIARIES: ({
      socialId,
      macarObjectId,
      cate,
      size,
    }: {
      socialId: string
      macarObjectId: string
      cate: string
      size: number
    }) =>
      `/macar-v2/admin/diaries?socialId=${encodeURIComponent(socialId)}&macarid=${macarObjectId}&cate=${encodeURIComponent(cate)}&size=${size}&cateFullMatch=true`,
    /**
     * 기록에 붙은 장소 하나를 조회한다.
     *
     * ⚠️ 기록 조회 응답에는 장소 **이름이 없고** 아이디(`placeid`)만 온다.
     * 그 아이디가 이 표의 `objectId`이고, 응답의 `name`이 화면에 쓸 장소 이름이다.
     * (업체 상세 조회로는 못 얻는다 — 예약으로 생긴 세차 기록 100건 중 1건만 그 표에서
     * 찾아진다는 것을 운영 데이터로 확인했다.)
     */
    PLACE_BY_OBJECT_ID: ({
      socialId,
      objectId,
    }: {
      socialId: string
      objectId: string
    }) =>
      `/macar-v2/admin/places?socialId=${encodeURIComponent(socialId)}&objectId=${encodeURIComponent(objectId)}`,
    DIARY_SUGGESTION: (macarId: number) =>
      `/macar-v2/maintenanceDiaries/suggestion?macarId=${macarId}`,
    TIPS: ({ typeName, typeType }: { typeName: string; typeType: string }) =>
      `/admin/maintenanceItemTip?typeName=${encodeURIComponent(typeName)}&type=${typeType}`,
    RECOMMENDATION: ({
      exposureArea,
      sections,
      limit,
    }: {
      exposureArea: string
      sections?: string[]
      limit?: number
    }) => {
      const sectionsQuery = (sections ?? [])
        .map(section => `&sections=${encodeURIComponent(section)}`)
        .join('')
      return `/social-v2/mycleContent/recommendation?exposureArea=${encodeURIComponent(exposureArea)}${sectionsQuery}${limit != null ? `&limit=${limit}` : ''}`
    },
  },
  RECORD_CERTIFICATE: {
    PUBLIC_INFO: (macarServerId: number) =>
      `/macar-v2/v1/macar/${macarServerId}/publicInfo`,
    GET: (macarServerId: number) =>
      `/macar-v2/v1/macar/${macarServerId}/recordCertificate`,
    CREATE: (macarServerId: number) =>
      `/macar-v2/v1/macar/${macarServerId}/recordCertificate`,
  },
  STANDARD: {
    DETAIL: (standardId: number) => `/standard-v2/v1/standard/${standardId}`,
    /**
     * 좌표 기준 14일 일별 날씨 예보 (오늘 포함).
     * 비로그인 허용이고, 실패해도 forecasts가 빈 배열로 온다.
     * 세차 예약 화면 날씨 아이콘과 같은 소스다.
     */
    WEATHER: ({
      latitude,
      longitude,
    }: {
      latitude: number
      longitude: number
    }) => `/standard-v2/weather?latitude=${latitude}&longitude=${longitude}`,
  },
  PARTNERS: {
    USER: {
      INFO: '/partners/user',
      UPDATE_OWNER: '/partners/user/updateOwner',
      UPDATE_MANAGER: '/partners/user/updateManager',
      VALIDATE_INVITE_CODE: '/partners/signup/validateApplicationCode',
      CHECK_DUPLICATE_USER_ID: '/partners/signup/checkDuplicateUserId',
      VALIDATE_OWNER: '/partners/user/validateOwner',
      UPDATE_STATUS: (serverId: number) => `/partners/user/${serverId}/status`,
    },
    SMS: {
      REQUEST_CODE: '/partners/findAndReset/verificationCode/send',
      CHECK_CODE: '/partners/findAndReset/verificationCode/validate',
      SIGNUP_REQUEST_CODE: '/partners/signup/verificationCode/send',
      SIGNUP_CHECK_CODE: '/partners/signup/verificationCode/validate',
      USER_REQUEST_CODE: '/partners/user/verificationCode/send',
      USER_CHECK_CODE: '/partners/user/verificationCode/validate',
    },
    ACCOUNT: {
      SIGNUP: '/partners/signup',
      RESET_PASSWORD: '/partners/findAndReset/resetPassword',
      VALIDATE_PASSWORD: '/partners/user/validatePassword',
      CHANGE_EMAIL: (serverId: number) => `/partners/user/${serverId}/email`,
      CHANGE_OWNER: (serverId: number) => `/partners/user/${serverId}/owner`,
      CHANGE_MANAGER: (serverId: number) =>
        `/partners/user/${serverId}/manager`,
    },
    APPLY: {
      DOCUMENTS: '/partners/applicationDocuments',
      DELETE_DOCUMENT: (serverId: number) =>
        `/partners/applicationDocuments/${serverId}`,
      PRESIGNED_URL: (serverId: number) =>
        `/partners/applicationDocuments/${serverId}/presignedUrl`,
      APPLICATION: '/partners/partnersApplications',
      APPLICATION_DETAIL: (applicationId: number) =>
        `/partners/partnersApplications/${applicationId}`,
    },
    ON_SITE: {
      DECODE_PLACE_HASH_ID: (placeHashId: string) =>
        `/web-service/hashid/place/${placeHashId}/decode`,
      ENCODE_PLACE_HASH_ID: (placeId: string) =>
        `/web-service/hashid/place/${placeId}`,
    },
  },
  MYCLE_CONTENT: {
    HOME: '/social-v2/mycleContent/home',
    CONTENTS: ({
      menu,
      page = 0,
      size = 20,
      sort,
      categoryId,
    }: IGetMycleContentsParams) =>
      `/social-v2/mycleContent?menu=${menu}&page=${page}&size=${size}${sort ? `&sort=${encodeURIComponent(sort)}` : ''}${categoryId != null ? `&categoryId=${categoryId}` : ''}`,
    CATEGORIES: ({ menu }: IGetMycleContentCategoriesParams) =>
      `/social-v2/mycleContent/categories?menu=${menu}`,
    SERIES: ({ code, page = 0, size = 20 }: IGetMycleContentsBySeriesParams) =>
      `/social-v2/mycleContent/series/${code}?page=${page}&size=${size}`,
    DETAIL: (contentId: number) => `/social-v2/mycleContent/${contentId}`,
    SITEMAP: '/social-v2/mycleContent/sitemap',
    LIKE: (contentId: number) => `/social-v2/mycleContent/${contentId}/like`,
    UNLIKE: (contentId: number) =>
      `/social-v2/mycleContent/${contentId}/unlike`,
  },
} as const

export const EXTERNAL_API_ENDPOINTS = {
  ADDRESS: {
    SEARCH: `${getApiPath('/api/address/search-keyword')}`,
    COORD: `${getApiPath('/api/address/by-coord')}`,
  },
  ANALYTICS: {
    FIREBASE: `${getApiPath('/api/analytics/firebase')}`,
    FIREBASE_PARTNERS: `${getApiPath('/api/analytics/firebase')}?project=partners`,
  },
  AUTOPEDIA: {
    AUTH: '/v2/auth/token',
  },
} as const
